import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from "reactstrap";
import { dynamicImage } from "../../../../../../Utils";
import { Image, P } from "../../../../../../AbstractElements";
import { getAPIURLAWS, getUserID } from "../../../../../../services/helper"; // Assuming helper provides the current user ID
import { toast } from "react-toastify";
import SvgIcon from "../../../../../../Utils/CommonComponents/CommonIcons/CommonSvgIcons";
import {
  deleteGroup,
  setLoading,
  updateGroup,
} from "../../../../../../ReduxToolkit/Reducers/ChatReducer";
import http from "../../../../../../services/http";
import Swal from "sweetalert2";

// GroupSettingsModal Component
const api_url = getAPIURLAWS();
const user_id = getUserID();
export default function GroupMembersModal({ isOpen, toggle, group }) {
  const { members, isLoading } = useSelector((state) => state.chat);
  const [selectedUsers, setSelectedUsers] = useState(group?.users || []);
  const [groupName, setGroupName] = useState(group?.groupName || "");
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (group) {
      setSelectedUsers(group.users);
      setGroupName(group.groupName);
    }
  }, [group]);

  const handleUserSelection = (userId) => {
    if (selectedUsers.some((user) => user.id === userId)) {
      setSelectedUsers(selectedUsers.filter((user) => user.id !== userId));
    } else {
      const userToAdd = members.find((member) => member.id === userId);
      if (userToAdd) {
        setSelectedUsers([...selectedUsers, userToAdd]);
      }
    }
  };

  const handleUpdateGroup = async () => {
    if (group) {
      try {
        dispatch(setLoading(true));
        const payload = {
          ...group,
          groupName,
          users: selectedUsers,
        };
        const { data: resp } = await http.post(
          `${api_url}/chats/groups`,
          payload
        );

        toggle();
        dispatch(setLoading(false));
        if (resp.success) {
          toast.success("Group updated successfully");
          dispatch(updateGroup(resp.data));
        }
      } catch (e) {
        dispatch(setLoading(false));
        console.error("Error while updating group", e);
      }
    }
  };

  const handleDeleteGroup = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const { data: resp } = await http.delete(`${api_url}/chats/groups`, {
            data: { groupId: group.groupId, user_id },
          });
          if (resp.success) {
            toast.success("Group deleted successfully");
            dispatch(deleteGroup(group.groupId));
            toggle();
          }
        } catch (e) {
          console.error("Error while deleting group", e);
        }
      }
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredMembers = members.filter((member) =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isMyGroup = group?.createdBy?.user_id === user_id;

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>Manage Group Members</ModalHeader>
      <ModalBody>
        {isMyGroup && (
          <div className="group-name-container mb-4">
            <Input
              type="text"
              placeholder="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="group-name-input"
            />

            <Button
              color="primary"
              className="update-group-button"
              disabled={!groupName.trim() || selectedUsers.length === 0}
              onClick={handleUpdateGroup}
            >
              Update
            </Button>

            <Button
              color="danger"
              className="delete-group-button ml-2"
              onClick={handleDeleteGroup}
            >
              Delete
            </Button>
          </div>
        )}

        <Input
          type="text"
          placeholder="Search members..."
          value={searchTerm}
          onChange={handleSearchChange}
          className="mb-3"
        />

        <div className="members-grid">
          {filteredMembers.map((member) => (
            <div
              key={member.id}
              className={`member-item ${
                selectedUsers.some((user) => user.id === member.id)
                  ? "selected-member"
                  : ""
              }`}
              onClick={() => handleUserSelection(member.id)}
            >
              <Image
                className="img-fluid rounded-circle member-image"
                src={member.src ? member.src : dynamicImage("avtar/3.jpg")}
                alt="user"
              />
              <span className="member-name">{member.name}</span>
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        {isMyGroup && (
          <Button
            color="primary"
            onClick={handleUpdateGroup}
            disabled={!groupName.trim() || selectedUsers.length === 0}
          >
            Update Group
          </Button>
        )}{" "}
        <Button color="danger" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
      <style jsx>{`
        .group-name-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
        }
        .group-name-input {
          flex: 1;
        }
        .update-group-button,
        .delete-group-button {
          flex-shrink: 0;
        }
        .members-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: center;
        }
        .member-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 10px;
          cursor: pointer;
          background-color: white;
          border-radius: 8px;
          transition: background-color 0.3s;
          border: 1px solid #ccc;
          width: 100px;
          height: 100px;
          box-sizing: border-box;
        }
        .member-item.selected-member {
          background-color: #28a745;
          border-color: #28a745;
        }
        .member-image {
          width: 50px;
          height: 50px;
        }
        .member-name {
          margin-top: 5px;
          font-weight: 500;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          width: 80px;
        }
      `}</style>
    </Modal>
  );
}
